import { DateTime } from "luxon";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "@/contexts/AuthProvider";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useDepartmentNurseTypes } from "@/hooks/useDepartmentNurseTypes";
import { useFormatPersonName } from "@/hooks/useNameFormat";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useRoleColors } from "@/hooks/useRoleColors";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { useScheduleDays } from "@/hooks/useScheduleDays";
import { useScheduleCacheEnabled } from "@/hooks/useScheduleWeekRules";
import {
	getNurseEmploymentDropdownItems,
	getRoleDropdownItems,
	type NurseEmploymentTypeKeys,
	type NurseFullRoleKeys,
} from "@/pages/Profile/v2/ProfessionalInfo/Dropdowns";
import type { DropdownItem } from "@/ui/Inputs/SystemDropDown";
import { trackEvent } from "@/utils/analytics";
import { isNurseActiveAnywhere, isNurseActiveInDepartment } from "@/utils/formatters";
import { EmploymentType, type HospitalData, type Nurse, NurseFullRole, type NurseInShift } from "@/utils/models";
import { rolesTakingRequest } from "@/utils/roles";
import { inverseRolesMap } from "@/utils/utilFunctions";
import { getSmsMaxChars, maxCharsDefault } from "../CharCounter";
import { usePostFacilityAnnouncement, usePostStaffMessage } from "../hooks";
import { type MessageFiltersType, MessageWorkingFilterEnum } from "./MessageFilter";
import type { NewBroadcastMessageModalProps } from "./NewBroadcastMessageModal";
import { type AnnouncementFilters, useFacilityAnnouncementScope } from "./useFacilityAnnouncementScope";

/**
 * A recipient must hold one of the selected roles — unless `selectedRoles` is `null`, the composer showing no Role
 * filter — and, while a job filter is active, one of the selected jobs.
 */
export const matchesBroadcastRoleOrJob = (nurse: Nurse, selectedRoles: NurseFullRoleKeys[] | null, activeJobIds: string[]): boolean => {
	const matchesRole = selectedRoles === null || selectedRoles.some((role) => inverseRolesMap[nurse.role] === role);
	if (activeJobIds.length === 0) return matchesRole;
	return matchesRole && !!nurse.jobIds?.some((nurseJobId) => activeJobIds.includes(nurseJobId));
};

/**
 * Whether the composer shows a Role filter. Not for a job department's posting: its pickup goes by job alone (backend
 * `check_role_match`), so the Jobs filter alone picks who hears of it — a Role filter there would suggest a limit
 * pickup doesn't keep (an unticked LPN holding the Nurse job can still claim the shift).
 */
export const showsBroadcastRoleFilter = (jobId: string | undefined, jobManagementEnabled: boolean): boolean => !jobId || !jobManagementEnabled;

/**
 * Role options the composer offers. Opened from a role department's posting (`jobId` given), the roles that can claim
 * it — those the posting names, or every role for one naming none or "all" — so an admin can narrow within them but
 * not widen the pool. Everywhere else every department role.
 */
export const broadcastRoleOptions = <T extends DropdownItem<NurseFullRoleKeys>>(
	departmentRoles: T[],
	jobId: string | undefined,
	shiftRoles: readonly string[],
): T[] => {
	if (!jobId) return departmentRoles;
	const claimingRoles = rolesTakingRequest(
		shiftRoles,
		departmentRoles.map((role) => NurseFullRole[role.value]),
		// Read as a role department's post: a job department's posting shows no Role filter, so its options go unused.
		false,
	);
	return departmentRoles.filter((role) => claimingRoles.includes(NurseFullRole[role.value]));
};

// Referentially stable "no job filter" value so the recipients memo doesn't recompute every render.
const NO_JOB_FILTER: string[] = [];

const debugNurseInShifts = (currentNurseShifts: NurseInShift[]) => {
	/*Dont remove this - this log is handy for debugging filters*/
	console.log(
		currentNurseShifts.map(
			(value) =>
				`${value.nurseId} ${value.role} ${value.startTime.toFormat("d-MM-yyyy")} ${value.startTime.toFormat("HH:mm")} - ${value.endTime.toFormat("HH:mm")}\n`,
		),
	);
};

const debugNurseInShiftNurseIds = (relevantNurseIds: number[], hospitalData: HospitalData) => {
	/*Dont remove this - this log is handy for debugging filters*/
	console.log(relevantNurseIds);
	console.log(hospitalData.nurses.filter((value) => relevantNurseIds.includes(value.id) && isNurseActiveAnywhere(value) && value.userId));
};

export const useBroadcastMessageState = ({
	onClose,
	initialRolesToSelect,
	nurseIdsToIgnore,
	initialFilters,
	defaultWorkingFilter = MessageWorkingFilterEnum.STAFF_THAT_WORKS,
	initialSmsMessage,
	addAppLink,
	initialSelectedNurseIds,
	lockRecipientSelection,
	jobId,
	allowFacilityScope,
}: NewBroadcastMessageModalProps) => {
	const { isSnf, hospitalData, chosenDepartment, showErrorSnackbar, timezone } = useHospitalData();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const { nurses, isLoading: isRosterLoading, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);
	const formatPersonName = useFormatPersonName();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const availableJobs = useMemo<DropdownItem<string>[]>(
		// DEFAULT: departments without job management have no jobs list — empty means the Jobs filter simply doesn't render.
		() => (chosenDepartment?.jobs ?? []).map((job) => ({ value: job.id, label: job.displayName, id: job.id })),
		[chosenDepartment],
	);
	// Pickup-context broadcasts seed the job filter with the request's job; generic broadcasts start inactive.
	const [selectedJobIds, setSelectedJobIds] = useState<string[]>(() => (jobId ? [jobId] : []));
	const handleJobsChange = useCallback((items?: string[]) => {
		setSelectedJobIds(items ?? []);
	}, []);
	const activeJobIds = jobManagementEnabled && selectedJobIds.length > 0 ? selectedJobIds : NO_JOB_FILTER;
	const showJobsFilter = jobManagementEnabled && availableJobs.length > 0;
	const jobFilterActive = activeJobIds.length > 0;
	const [filters, setFilters] = useState<{ id: string; filterData: MessageFiltersType }[]>(() =>
		initialFilters
			? initialFilters.map((value) => ({
					id: uuidv4(),
					filterData: value,
				}))
			: [],
	);
	const nurseEmploymentDropdownItems = getNurseEmploymentDropdownItems(isSnf);
	const { userInfo } = useAuth();
	const roleColor = useRoleColors();
	const showRolesFilter = showsBroadcastRoleFilter(jobId, jobManagementEnabled);
	const availableRoles = useMemo(
		() =>
			broadcastRoleOptions(
				getRoleDropdownItems(departmentNurseTypes, roleColor),
				jobId,
				// DEFAULT: a posting passing no roles restricts no role — `rolesTakingRequest` reads `[]` as every role.
				initialRolesToSelect ?? [],
			),
		[departmentNurseTypes, roleColor, jobId, initialRolesToSelect],
	);

	// Null until the user picks: then every role offered (or the ones asked for), which grow once the roster loads.
	const [pickedRoles, setPickedRoles] = useState<NurseFullRoleKeys[] | null>(null);
	const selectedRoles = useMemo<NurseFullRoleKeys[]>(() => {
		if (pickedRoles) return pickedRoles;
		const offeredRoles = availableRoles.map((role) => role.value);
		// DEFAULT: opened from a posting, every role offered can claim it, so all start ticked; `initialRolesToSelect`
		// pre-selects only in the general composer.
		if (initialRolesToSelect && !jobId) return offeredRoles.filter((role) => initialRolesToSelect.includes(NurseFullRole[role]));
		return offeredRoles;
	}, [pickedRoles, initialRolesToSelect, jobId, availableRoles]);
	const handleRolesChange = useCallback((items?: NurseFullRoleKeys[]) => {
		setPickedRoles(items ?? []);
	}, []);
	// `null` while the Role filter is hidden: role then narrows no one.
	const recipientRoles = showRolesFilter ? selectedRoles : null;
	const [selectedEmploymentTypes, setSelectedTypes] = useState<NurseEmploymentTypeKeys[]>(nurseEmploymentDropdownItems.map((value) => value.value));
	const handleEmploymentTypesChange = useCallback((items?: NurseEmploymentTypeKeys[]) => {
		setSelectedTypes(items ?? []);
	}, []);

	// The same choice, as an announcement to the building sends it: a filter with everything ticked doesn't
	// narrow (null), so it also reaches roles this department doesn't list and staff in no department.
	const announcementFilters = useMemo<AnnouncementFilters>(
		() => ({
			roles: !recipientRoles || recipientRoles.length === availableRoles.length ? null : recipientRoles.map((role) => NurseFullRole[role]),
			jobIds: activeJobIds.length > 0 ? activeJobIds : null,
			employmentTypes:
				selectedEmploymentTypes.length === nurseEmploymentDropdownItems.length ? null : selectedEmploymentTypes.map((type) => EmploymentType[type]),
		}),
		[recipientRoles, availableRoles.length, activeJobIds, selectedEmploymentTypes, nurseEmploymentDropdownItems.length],
	);
	const facilityScope = useFacilityAnnouncementScope(allowFacilityScope, announcementFilters);
	const { isFacilityAnnouncement } = facilityScope;

	const [selectedNurseList, setSelectedNurseList] = useState<Nurse[]>([]);
	// With the cache on, the filters' dates are read from the cached days spanning them.
	const isCacheEnabled = useScheduleCacheEnabled();
	const filterDates = filters.map((filter) => filter.filterData.dateFilter);
	// DEFAULT: today with no filter, when no day is read at all.
	const filterDays = useScheduleDays(DateTime.min(...filterDates) ?? DateTime.now(), DateTime.max(...filterDates) ?? DateTime.now());
	const isFilterDayLoading = isCacheEnabled && filters.length > 0 && filterDays.isLoading;
	const nursesOptions = useMemo(() => {
		// Until the filtered days arrive nobody can be told apart, so no one is offered rather than everyone.
		if (isFilterDayLoading) return [];
		const relevantNurseIds: number[] = [];
		if (filters.length > 0) {
			const isFilteredDate = (date: DateTime) => filters.some((currFilter) => currFilter.filterData.dateFilter.hasSame(date, "day"));
			const nurseInShiftsForDates: Pick<NurseInShift, "nurseId" | "startTime" | "endTime">[] = isCacheEnabled
				? filterDays.days.filter((day) => isFilteredDate(day.date)).flatMap((day) => day.scheduled)
				: hospitalData.weeks.flatMap<NurseInShift>((week) =>
						week.weeklyShifts.flatMap((weeklyShift) => weeklyShift.days.filter((day) => isFilteredDate(day.date)).flatMap((day) => day.nurses)),
					);
			const nurseIds = new Set(nurses.map((nurse) => nurse.id));
			nurseIds.forEach((currentNurseId) => {
				const currentNurseShifts = nurseInShiftsForDates.filter((value) => value.nurseId === currentNurseId);
				if (
					filters.every((checkedFilter) => {
						const relevantShifts = currentNurseShifts.filter((shift) => {
							return shift.startTime.hasSame(checkedFilter.filterData.dateFilter, "day");
						});
						return (
							(relevantShifts.length === 0 && checkedFilter.filterData.workFilter === MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK) ||
							(relevantShifts.length > 0 &&
								relevantShifts.every((checkedShift) => {
									const nurseStart = checkedShift.startTime;
									const nurseEnd = checkedShift.endTime.plus({ days: checkedShift.endTime <= checkedShift.startTime ? 1 : 0 });

									const filterStart = checkedFilter.filterData.timeFilter.start.set({
										year: checkedFilter.filterData.dateFilter.year,
										month: checkedFilter.filterData.dateFilter.month,
										day: checkedFilter.filterData.dateFilter.day,
									});

									const filterEndCalc = checkedFilter.filterData.timeFilter.end.set({
										year: checkedFilter.filterData.dateFilter.year,
										month: checkedFilter.filterData.dateFilter.month,
										day: checkedFilter.filterData.dateFilter.day,
									});

									const filterEnd = filterEndCalc <= filterStart ? filterEndCalc.plus({ days: 1 }) : filterEndCalc;

									const isNurseTimesInFilterRange = nurseStart < filterEnd && nurseEnd > filterStart;
									return checkedFilter.filterData.workFilter === MessageWorkingFilterEnum.STAFF_THAT_WORKS
										? isNurseTimesInFilterRange
										: !isNurseTimesInFilterRange;
								}))
						);
					})
				) {
					relevantNurseIds.push(currentNurseId);
					// debugNurseInShifts(currentNurseShifts);
				}
			});
			// debugNurseInShiftNurseIds(relevantNurseIds, hospitalData);
		}
		return nurses
			.filter(
				(value) =>
					chosenDepartment &&
					isNurseActiveInDepartment(value, chosenDepartment.department_id) &&
					!!value.userId &&
					!!value.phoneNumber &&
					matchesBroadcastRoleOrJob(value, recipientRoles, activeJobIds) &&
					!!selectedEmploymentTypes.find((empType) => value.employmentType === EmploymentType[empType]) &&
					(filters.length > 0 ? relevantNurseIds.includes(value.id) : true) &&
					((nurseIdsToIgnore?.length ?? 0) > 0 ? !nurseIdsToIgnore?.includes(value.id) : true),
			)
			.sort((a, b) => formatPersonName(a).localeCompare(formatPersonName(b)))
			.map<DropdownItem<Nurse>>((value) => ({
				value: value,
				label: () => formatPersonName(value),
				id: value.id,
			}));
	}, [
		nurses,
		recipientRoles,
		selectedEmploymentTypes,
		filters,
		hospitalData.weeks,
		isCacheEnabled,
		isFilterDayLoading,
		filterDays.days,
		nurseIdsToIgnore,
		chosenDepartment,
		activeJobIds,
		formatPersonName,
	]);

	useEffect(() => {
		const baseSelectable = nursesOptions.map((value) => value.value).filter((n) => n.userId && n.phoneNumber);
		if (initialSelectedNurseIds && initialSelectedNurseIds.length > 0) {
			const idSet = new Set(initialSelectedNurseIds);
			setSelectedNurseList(baseSelectable.filter((n) => idSet.has(n.id)));
			return;
		}
		setSelectedNurseList(baseSelectable);
	}, [nursesOptions, initialSelectedNurseIds]);
	const onDropdownSelectionChange = useCallback(
		(selectedValues: Nurse[]) => {
			if (lockRecipientSelection) return;
			setSelectedNurseList(selectedValues.filter((n) => n.userId && n.phoneNumber));
		},
		[lockRecipientSelection],
	);

	const [messageValue, setMessageValue] = useState<string>(initialSmsMessage ?? "");
	const handleMessageChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		if (event.target.value.length > maxCharsDefault) {
			return;
		}
		setMessageValue(event.target.value);
	}, []);

	const [showBackWarning, setShowBackWarning] = useState<boolean>(false);
	const toggleBackWarning = useCallback(() => {
		setShowBackWarning((prev) => !prev);
	}, []);

	const handleCancelOrEscape = useCallback(() => {
		if (messageValue.trim().length === 0) {
			onClose();
		} else {
			toggleBackWarning();
		}
	}, [messageValue, onClose, toggleBackWarning]);

	const { mutateAsync: postBroadcastMessage, isPending: isPostingToStaff, isSuccess: isPostedToStaff } = usePostStaffMessage();
	const { mutate: postFacilityAnnouncement, isPending: isAnnouncing, isSuccess: isAnnounced } = usePostFacilityAnnouncement();
	const isLoading = isPostingToStaff || isAnnouncing;
	const isSuccess = isPostedToStaff || isAnnounced;

	const sendingDisabled = useMemo(() => {
		const hasRecipients = isFacilityAnnouncement ? facilityScope.hasRecipients : selectedNurseList.length > 0;
		return messageValue.trim().length === 0 || !hasRecipients || messageValue.length > getSmsMaxChars(messageValue);
	}, [messageValue, selectedNurseList.length, isFacilityAnnouncement, facilityScope.hasRecipients]);

	const handleSendMessage = useCallback(async () => {
		if (sendingDisabled) {
			showErrorSnackbar("Please enter a message and select at least one staff member.", {}, {});
			return;
		}
		if (isFacilityAnnouncement) {
			// The hook reports the outcome either way; the composer closes only once the texts went out.
			postFacilityAnnouncement({ message: messageValue.trim(), audience: facilityScope.audience }, { onSuccess: () => onClose(true) });
			return;
		}
		await postBroadcastMessage({
			message: messageValue.trim(),
			userIds: selectedNurseList.map((nurse) => nurse.userId).filter((res) => res !== undefined),
			tempId: uuidv4(),
			addAppLink,
		});
		trackEvent("broadcast_message_sent", {
			number_of_recipients: selectedNurseList.length,
			message: messageValue,
		});
		// `true` = a message actually went out. Callers that record something on the strength of a sent
		// message (a Move request, say) must not record it when the composer is merely dismissed.
		onClose(true);
	}, [
		sendingDisabled,
		messageValue,
		selectedNurseList,
		postBroadcastMessage,
		showErrorSnackbar,
		onClose,
		addAppLink,
		isFacilityAnnouncement,
		postFacilityAnnouncement,
		facilityScope.audience,
	]);

	const addFilterClicked = useCallback(() => {
		setFilters((prev) => [
			...prev,
			{
				id: uuidv4(),
				filterData: {
					workFilter: defaultWorkingFilter,
					dateFilter: DateTime.now().setZone(timezone),
					timeFilter: {
						start: DateTime.now().setZone(timezone).startOf("day").set({ hour: 6 }),
						end: DateTime.now().setZone(timezone).startOf("day").set({ hour: 6 }).plus({ days: 1 }).minus({ minutes: 1 }),
					},
				},
			},
		]);
	}, [timezone, defaultWorkingFilter]);

	const deleteFilterClicked = useCallback((id: string) => {
		setFilters((prev) => prev.filter((filterAndId) => filterAndId.id !== id));
	}, []);

	const handleFilterChanged = useCallback((filters: MessageFiltersType, filterUuid: string) => {
		setFilters((prev) => {
			const index = prev.findIndex((filterAndId) => filterAndId.id === filterUuid);
			if (index !== -1) {
				const newFilters = [...prev];
				newFilters[index] = { id: filterUuid, filterData: filters };
				return newFilters;
			}
			return prev;
		});
	}, []);

	return {
		filters,
		nurseEmploymentDropdownItems,
		selectedRoles,
		handleRolesChange,
		selectedEmploymentTypes,
		handleEmploymentTypesChange,
		selectedNurseList,
		nursesOptions,
		onDropdownSelectionChange,
		messageValue,
		handleMessageChange,
		showBackWarning,
		toggleBackWarning,
		handleCancelOrEscape,
		isLoading,
		sendingDisabled,
		handleSendMessage,
		addFilterClicked,
		deleteFilterClicked,
		handleFilterChanged,
		availableRoles,
		isSuccess,
		availableJobs,
		selectedJobIds,
		handleJobsChange,
		showJobsFilter,
		showRolesFilter,
		jobFilterActive,
		isRosterLoading,
		isRosterError,
		facilityScope,
	};
};
